/**
 * The alternatives one AI run produced, addressed by the field that shows them.
 *
 * A generation writes two different things into the configuration. The slides,
 * the colours and the chosen call to action are the ad: one option picked out of
 * several. `suggestions` is the rest of the run — every headline the copy step
 * wrote for each slide, every call to action, every image the asset assessment
 * approved — and it stays complete regardless of which of them the ad uses.
 * That is why nothing here reads a slide or a selected value.
 *
 * A field is matched by its own declared path, so `headline` inside the slides
 * collection takes that slide's array and `cta.text` takes the shared one. The
 * slide index comes from the path the renderer built (`slides.0.headline`),
 * which keeps the template schema out of it — no package has to declare where
 * its alternatives live.
 */

// Field path within a collection item → the suggestions array behind it. A path
// mapped to a per-slide array is looked up by the item's own index; one mapped
// to a flat array is shared by every slide.
const PER_SLIDE = { headline: 'headlineOptions', 'elements.slogan.text': 'headlineOptions' }
const SHARED = { 'cta.text': 'ctaOptions' }

/** Trimmed non-empty strings, in order, without repeats. */
export function distinctText(values) {
  const seen = new Set()
  const result = []
  for (const value of Array.isArray(values) ? values : []) {
    if (typeof value !== 'string') continue
    const text = value.trim()
    if (!text || seen.has(text)) continue
    seen.add(text)
    result.push(text)
  }
  return result
}

/** The collection index in a rendered path, or null outside a collection. */
export function slideIndexFromPath(path) {
  const match = /(?:^|\.)(\d+)\./.exec(typeof path === 'string' ? path : '')
  return match ? Number(match[1]) : null
}

/**
 * The alternatives to offer under one field, or an empty list for a field that
 * has none. Values are plain text; a rich-text field renders them as its own
 * markup once chosen.
 */
export function fieldSuggestions(configuration, field, path) {
  const suggestions = configuration?.suggestions
  if (!suggestions || typeof suggestions !== 'object') return []

  const fieldPath = field?.path
  if (typeof fieldPath !== 'string') return []

  if (SHARED[fieldPath]) return distinctText(suggestions[SHARED[fieldPath]])

  const perSlide = PER_SLIDE[fieldPath]
  if (!perSlide) return []

  const index = slideIndexFromPath(path)
  if (index === null) return []

  const rows = suggestions[perSlide]
  return Array.isArray(rows) ? distinctText(rows[index]) : []
}

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i

/**
 * The palette the run extracted from the advertiser's own material.
 *
 * Offered on every colour field rather than only the one the run assigned it
 * to: which slot a colour belongs in is a design judgement, and an operator
 * moving the brand's orange from the panel to the accent should not have to
 * find the hex again. The colours the ad already uses come first, because
 * those are the ones being adjusted.
 *
 * Anything that is not a plain hex is dropped — the swatch row is the one
 * place a malformed value would reach the picker and stick.
 */
function distinctColors(values) {
  const seen = new Set()
  const result = []
  for (const value of Array.isArray(values) ? values : []) {
    if (typeof value !== 'string') continue
    const color = value.trim().toLowerCase()
    if (!HEX_COLOR.test(color) || seen.has(color)) continue
    seen.add(color)
    result.push(color)
  }
  return result
}

/**
 * Only what this run measured from the advertiser's own material.
 *
 * Separate from `colorSuggestions` because the two answer different questions.
 * The picker asks what a person might reasonably choose, so it offers the
 * colours already in the ad as well; the row under the field says "here is
 * what we found on your website", and a template default is not that. It is
 * also why this returns nothing before the first generation, exactly as the
 * chips under a slogan do.
 */
export function extractedColorSuggestions(configuration) {
  return distinctColors(configuration?.suggestions?.colorCandidates)
}

export function colorSuggestions(configuration) {
  const assigned = Object.values(configuration?.data?.colors || configuration?.design?.colors || {})
  return distinctColors([
    ...assigned,
    ...(configuration?.suggestions?.colorCandidates || []),
  ])
}

// The on-screen order of the five roles: Button Color, Button Text, Background, Slogan, Accent.
const ROLE_ORDER = ['c1', 'c4', 'c2', 'c3', 'acc']
export const HOUSE_SWATCHES = ['#d00b65', '#212529', '#ffffff', '#3c64a2', '#f2efe9', '#868e96', '#00922f']

/**
 * The Suggestions row and picker palette of the Brand colour fields: the colours measured for
 * the advertiser, then the ad's palette, then house colours; seven at most. The caller keeps the
 * result for as long as the palette source is the same (see `paletteSourceKey`), so picking a
 * colour never reorders the row: a pick changes `data.colors`, not this list.
 */
export function stablePaletteSuggestions(configuration, limit = 7) {
  const colors = configuration?.data?.colors || configuration?.design?.colors || {}
  return distinctColors([
    ...(configuration?.suggestions?.colorCandidates || []),
    ...ROLE_ORDER.map((role) => colors[role]),
    ...HOUSE_SWATCHES,
  ]).slice(0, limit)
}

/**
 * What makes the palette row start again: another template or a generation run (new measured
 * candidates). Not the ad id — a first save would otherwise reshuffle the row once.
 */
export function paletteSourceKey(configuration) {
  return JSON.stringify([
    configuration?.template?.releaseKey || configuration?.template?.id || null,
    configuration?.suggestions?.colorCandidates || null,
  ])
}

/**
 * The images the asset assessment approved, as library entries.
 *
 * The candidate arrays hold asset ids; the registry holds the durable URL each
 * one was published under. An id with no entry is skipped rather than shown as
 * a broken thumbnail.
 */
export function approvedLibraryImages(configuration, assetSourceUrls, { logos = false } = {}) {
  const candidates = configuration?.suggestions?.[logos ? 'logoCandidates' : 'imageCandidates']
  if (!Array.isArray(candidates)) return []

  const registry = configuration?.assets
  const urls = assetSourceUrls || {}
  const seen = new Set()
  const result = []

  for (const assetId of candidates) {
    if (typeof assetId !== 'string') continue
    const fromMap = typeof urls[assetId] === 'string' ? urls[assetId] : ''
    const fromRegistry = typeof registry?.[assetId]?.url === 'string' ? registry[assetId].url : ''
    const url = (fromMap || fromRegistry).trim()
    if (!url || seen.has(url)) continue
    seen.add(url)
    const media = (configuration?.data?.slides || configuration?.slides || [])
      .map((slide) => slide.media || { ...slide, sourceAssetId: slide.imageAssetId })
      .find((entry) => entry.sourceAssetId === assetId)
    const originUrl = registry?.[assetId]?.sourceEvidence?.sourceUrl
    result.push({ url, assetId, source: 'ai',
      ...(typeof originUrl === 'string' && originUrl ? { originUrl } : {}),
      ...(media?.crop ? { crop: media.crop } : {}),
      ...((media?.focalPoint || registry?.[assetId]?.focalPoint)
        ? { focalPoint: media?.focalPoint || registry[assetId].focalPoint } : {}),
    })
  }

  return result
}

/**
 * One library list out of the two sources, without showing anything twice.
 *
 * The approved images come first: they were assessed for this advertiser and
 * this format, while the harvested ones are everything the site happened to
 * carry. A URL present in both is kept once, as the approved entry.
 */
export function mergeLibraryImages(approved, harvested) {
  const seen = new Set()
  const result = []

  for (const entry of [...(approved || []), ...(harvested || [])]) {
    const url = typeof entry?.url === 'string' ? entry.url.trim() : ''
    if (!url || seen.has(url)) continue
    seen.add(url)
    // A website picture kept in the ad's library (AD-234) stands for its harvested original.
    if (entry.originUrl) seen.add(entry.originUrl)
    result.push(entry.source ? entry : { ...entry, source: 'website' })
  }

  return result
}

const LIBRARY_LIMIT = 200

/**
 * AD-234: an image the user uploaded or imported into a field joins the ad's library (the
 * candidate list the library shows), so replacing it later does not lose it; it is saved with
 * the ad, used or not. `sourceUrl` records the website or stock picture it was imported from.
 */
export function withLibraryImage(configuration, assetId, { logos = false, sourceUrl = null } = {}) {
  if (!configuration || !assetId) return configuration
  const key = logos ? 'logoCandidates' : 'imageCandidates'
  const current = Array.isArray(configuration.suggestions?.[key]) ? configuration.suggestions[key] : []
  if (current.includes(assetId)) return configuration
  const next = {
    ...configuration,
    suggestions: { ...(configuration.suggestions || {}), [key]: [...current, assetId].slice(-LIBRARY_LIMIT) },
  }
  if (sourceUrl) {
    next.assets = { ...(configuration.assets || {}), [assetId]: {
      ...(configuration.assets?.[assetId] || {}), id: assetId,
      sourceEvidence: { sourceId: assetId, sourceUrl, transformation: 'import', version: 1 },
    } }
  }
  return next
}

/**
 * AD-234: a new run for the same advertiser adds its pictures to the library instead of
 * replacing it; only another advertiser starts it again (clearImageSelections).
 */
export function withKeptLibrary(previous, next) {
  if (!previous?.suggestions || !next) return next
  let result = next
  for (const key of ['imageCandidates', 'logoCandidates']) {
    const before = Array.isArray(previous.suggestions[key]) ? previous.suggestions[key] : []
    if (!before.length) continue
    const now = Array.isArray(result.suggestions?.[key]) ? result.suggestions[key] : []
    result = {
      ...result,
      suggestions: { ...(result.suggestions || {}), [key]: [...new Set([...now, ...before])].slice(0, LIBRARY_LIMIT) },
    }
  }
  const missing = Object.entries(previous.assets || {}).filter(([id]) => !result.assets?.[id])
  if (missing.length) result = { ...result, assets: { ...Object.fromEntries(missing), ...(result.assets || {}) } }
  return result
}
